import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * DateRangePicker - A reusable date range picker component
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether to show the picker
 * @param {Function} props.onClose - Callback when closing the picker
 * @param {string} props.startDate - Start date in YYYY-MM-DD format
 * @param {string} props.endDate - End date in YYYY-MM-DD format
 * @param {Function} props.onDateRangeSelect - Callback when date range is selected ({ start, end })
 * @param {Object} props.buttonRef - Ref to the button that triggers the picker
 */
const DateRangePicker = ({
  show,
  onClose,
  startDate,
  endDate,
  onDateRangeSelect,
  buttonRef,
}) => {
  // Helper to parse date string (YYYY-MM-DD) to local Date object
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempStartDate, setTempStartDate] = useState(
    startDate ? parseDateString(startDate) : null
  );
  const [tempEndDate, setTempEndDate] = useState(
    endDate ? parseDateString(endDate) : null
  );
  const calendarRef = useRef(null);
  const [, forceUpdate] = useState({}); // For forcing re-render on scroll/resize

  // Reset temp dates when props change
  useEffect(() => {
    setTempStartDate(startDate ? parseDateString(startDate) : null);
    setTempEndDate(endDate ? parseDateString(endDate) : null);
  }, [startDate, endDate]);

  // Calculate position synchronously during render to prevent flicker
  const calculatePosition = () => {
    if (!show || !buttonRef?.current) {
      return { vertical: "bottom", horizontal: "right" };
    }

    const buttonElement = buttonRef.current;
    if (!buttonElement) {
      return { vertical: "bottom", horizontal: "right" };
    }

    const buttonRect = buttonElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    // Estimate picker dimensions
    // On mobile: single calendar ~280px wide, ~420px tall
    // On small desktop (md): single calendar ~288px wide, ~520px tall
    // On larger desktop (lg+): two calendars ~600px wide, ~520px tall
    const isMobile = windowWidth < 640;
    const isSmallDesktop = windowWidth >= 640 && windowWidth < 1024;
    const isLargeDesktop = windowWidth >= 1024;

    const pickerWidth = isMobile ? 280 : isSmallDesktop ? 288 : 600;
    const pickerHeight = isMobile ? 420 : 520;

    // Calculate vertical position
    const spaceBelow = windowHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const buffer = 20; // 20px buffer
    const verticalPosition =
      spaceBelow < pickerHeight + buffer && spaceAbove > spaceBelow
        ? "top"
        : "bottom";

    // Calculate horizontal position
    const spaceRight = windowWidth - buttonRect.right;
    const spaceLeft = buttonRect.left;
    const horizontalPosition =
      spaceRight < pickerWidth + buffer && spaceLeft > spaceRight
        ? "left"
        : "right";

    return { vertical: verticalPosition, horizontal: horizontalPosition };
  };

  // Calculate position synchronously during render (no state, no flicker)
  const currentPosition = show
    ? calculatePosition()
    : { vertical: "bottom", horizontal: "right" };

  // Update position on scroll and resize (force re-render to recalculate)
  useEffect(() => {
    if (!show) return;

    const handleUpdate = () => {
      // Force re-render to recalculate position
      forceUpdate({});
    };

    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);

    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
    };
  }, [show]);

  if (!show) return null;

  const changeMonth = (offset) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + offset);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const isDateInRange = (date) => {
    if (!tempStartDate || !tempEndDate) return false;
    const dateTime = date.getTime();
    const startTime = tempStartDate.getTime();
    const endTime = tempEndDate.getTime();
    return dateTime >= startTime && dateTime <= endTime;
  };

  const isDateSelected = (date) => {
    if (!tempStartDate && !tempEndDate) return false;
    const dateStr = date.toDateString();
    return (
      (tempStartDate && dateStr === tempStartDate.toDateString()) ||
      (tempEndDate && dateStr === tempEndDate.toDateString())
    );
  };

  const isStartDate = (date) => {
    if (!tempStartDate) return false;
    return date.toDateString() === tempStartDate.toDateString();
  };

  const isEndDate = (date) => {
    if (!tempEndDate) return false;
    return date.toDateString() === tempEndDate.toDateString();
  };

  const isDateInFuture = (date) => {
    // Get today in Asia/Thimphu timezone
    const now = new Date();
    const thimphuToday = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" })
    );
    thimphuToday.setHours(0, 0, 0, 0);
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    return dateToCheck > thimphuToday;
  };

  const handleDateClick = (date) => {
    // Don't allow selecting future dates
    if (isDateInFuture(date)) return;

    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Start new selection
      setTempStartDate(date);
      setTempEndDate(null);
    } else if (tempStartDate && !tempEndDate) {
      // Complete the range
      let finalStartDate = tempStartDate;
      let finalEndDate = date;

      if (date < tempStartDate) {
        // If selected date is before start, swap them
        finalStartDate = date;
        finalEndDate = tempStartDate;
      }

      setTempStartDate(finalStartDate);
      setTempEndDate(finalEndDate);

      // Auto-apply when both dates are selected
      const now = new Date();
      const thimphuToday = new Date(
        now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" })
      );
      thimphuToday.setHours(0, 0, 0, 0);

      // Cap dates to today if they're in the future
      if (finalEndDate > thimphuToday) {
        finalEndDate = thimphuToday;
      }
      if (finalStartDate > thimphuToday) {
        finalStartDate = thimphuToday;
      }

      onDateRangeSelect({
        start: formatDateString(finalStartDate),
        end: formatDateString(finalEndDate),
      });
      onClose();
    }
  };

  const renderCalendarDays = (monthOffset = 0) => {
    const displayMonth = new Date(currentMonth);
    displayMonth.setMonth(displayMonth.getMonth() + monthOffset);
    const { daysInMonth, startingDayOfWeek, year, month } =
      getDaysInMonth(displayMonth);

    const days = [];
    const totalCells = Math.ceil((daysInMonth + startingDayOfWeek) / 7) * 7;

    for (let i = 0; i < totalCells; i++) {
      const dayNumber = i - startingDayOfWeek + 1;
      const isValidDay = dayNumber > 0 && dayNumber <= daysInMonth;

      if (!isValidDay) {
        // Empty cell for padding
        days.push(<div key={`empty-${i}`} className="h-9 sm:h-10" />);
        continue;
      }

      const date = new Date(year, month, dayNumber);
      date.setHours(0, 0, 0, 0);
      const inRange = isDateInRange(date);
      const selected = isDateSelected(date);
      const isFuture = isDateInFuture(date);
      const isStart = isStartDate(date);
      const isEnd = isEndDate(date);

      // Determine border radius classes
      let borderRadiusClass = "";
      if (selected || inRange) {
        if (isStart && isEnd) {
          // Single date selected - full border radius
          borderRadiusClass = "rounded";
        } else if (isStart) {
          // First date - left border radius
          borderRadiusClass = "rounded-l";
        } else if (isEnd) {
          // Last date - right border radius
          borderRadiusClass = "rounded-r";
        }
        // Middle dates in range have no border radius
      }

      days.push(
        <button
          key={`day-${dayNumber}`}
          onClick={() => handleDateClick(date)}
          disabled={isFuture}
          className={`h-9 sm:h-10 flex items-center justify-center text-xs sm:text-sm transition-colors ${borderRadiusClass} ${
            isFuture
              ? "text-gray-300 cursor-not-allowed"
              : selected
              ? "bg-black text-white font-semibold"
              : inRange
              ? "bg-blue-50 text-blue-600"
              : "text-gray-700 hover:bg-gray-100"
          }`}
        >
          {dayNumber}
        </button>
      );
    }

    return days;
  };

  const renderCalendar = (monthOffset = 0) => {
    const displayMonth = new Date(currentMonth);
    displayMonth.setMonth(displayMonth.getMonth() + monthOffset);
    const monthName = displayMonth.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    const days = renderCalendarDays(monthOffset);

    return (
      <div className="w-64 sm:w-72" key={`calendar-${monthOffset}`}>
        <div className="text-center font-medium text-sm sm:text-base text-gray-900 mb-2 sm:mb-3">
          {monthName}
        </div>
        <div className="grid grid-cols-7 mb-1 sm:mb-2">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <div
              key={day}
              className="h-9 sm:h-10 flex items-center justify-center text-[10px] sm:text-xs text-gray-500 font-medium"
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">{days}</div>
      </div>
    );
  };

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Quick selection handlers
  const handleQuickSelect = (type) => {
    // Get today in Asia/Thimphu timezone
    const now = new Date();
    const thimphuToday = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" })
    );
    thimphuToday.setHours(0, 0, 0, 0);
    let newStartDate = null;
    let newEndDate = thimphuToday;

    switch (type) {
      case "week":
        // One week including today = today and 6 days before (7 days total)
        const weekAgo = new Date(thimphuToday);
        weekAgo.setDate(thimphuToday.getDate() - 6);
        newStartDate = weekAgo;
        break;
      case "month":
        // One month including today = today and the previous month's same date
        const monthAgo = new Date(thimphuToday);
        monthAgo.setMonth(thimphuToday.getMonth() - 1);
        monthAgo.setDate(thimphuToday.getDate());
        newStartDate = monthAgo;
        break;
      case "threeMonths":
        // Three months including today = today and 3 months before
        const threeMonthsAgo = new Date(thimphuToday);
        threeMonthsAgo.setMonth(thimphuToday.getMonth() - 3);
        threeMonthsAgo.setDate(thimphuToday.getDate());
        newStartDate = threeMonthsAgo;
        break;
      default:
        return;
    }

    setTempStartDate(newStartDate);
    setTempEndDate(newEndDate);

    onDateRangeSelect({
      start: formatDateString(newStartDate),
      end: formatDateString(newEndDate),
    });
    onClose();
  };

  // Position classes based on available space (calculated synchronously during render)
  const getPositionClasses = () => {
    const verticalClass =
      currentPosition.vertical === "top" ? "bottom-full mb-2" : "top-full mt-2";

    // Horizontal positioning: if no space on right, align right; if space on right, align left
    const horizontalClass =
      currentPosition.horizontal === "left"
        ? "right-0" // No space on right - align picker's right edge to button's right edge
        : "left-0"; // Space on right - align picker's left edge to button's left edge

    return `${verticalClass} ${horizontalClass}`;
  };

  const positionClasses = getPositionClasses();

  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} />
      <div
        ref={calendarRef}
        className={`absolute ${positionClasses} bg-white rounded-lg shadow-xl border border-gray-200 p-3 sm:p-4 lg:p-6 z-20 max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-4rem)] lg:max-w-none`}
      >
        {/* Calendars - One on mobile/small desktop, two on larger desktop */}
        <div className="flex gap-3 sm:gap-4 lg:gap-6 items-start">
          {/* Navigation Arrow Left */}
          <div className="flex items-center">
            <button
              onClick={() => changeMonth(-1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          {/* First Calendar - Always visible */}
          {renderCalendar(0)}

          {/* Second Calendar - Hidden on mobile/small desktop, visible on larger desktop (lg+) */}
          <div className="hidden lg:block">{renderCalendar(1)}</div>

          {/* Navigation Arrow Right */}
          <div className="flex items-center">
            <button
              onClick={() => changeMonth(1)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Quick Selection Buttons */}
        <div className="flex gap-2 mt-6 pt-4 border-t">
          <button
            onClick={() => handleQuickSelect("week")}
            className="px-2 py-1.5 sm:px-3 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            One Week
          </button>
          <button
            onClick={() => handleQuickSelect("month")}
            className="px-2 py-1.5 sm:px-3 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            One Month
          </button>
          <button
            onClick={() => handleQuickSelect("threeMonths")}
            className="px-2 py-1.5 sm:px-3 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Three Months
          </button>
        </div>
      </div>
    </>
  );
};

export default DateRangePicker;
